<template>
    <div id="image-settings">
        <div class="form-field">
            <label for="image-input" class="form-label">{{ $t("form.image") }}</label>
            <input type="file" id="image-input" class="form-input" @change="uploadFile" />
            <div v-if="logo">
                <img :src="logo" alt="Selected Image" style="max-width: 100%; margin-top: 10px;" />
            </div>
        </div>
        <div class="form-field">
            <label class="form-label">{{ $t("form.size") }}</label>
            <div class="size-settings">
                <div class="size-input">
                    <label for="width-input">{{ $t("form.width") }}</label>
                    <div class="input-with-unit">
                        <input type="number" id="width-input" v-model="imageWidth" class="form-input"
                               @input="updateImageWidth(imageWidth)" />
                        <span class="unit">PX</span>
                    </div>
                </div>
                <div class="size-input">
                    <label for="height-input">{{ $t("form.height") }}</label>
                    <div class="input-with-unit">
                        <input type="number" id="height-input" v-model="imageHeight" class="form-input"
                               @input="updateImageHeight" />
                        <span class="unit">PX</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-field">
            <label class="form-label">{{ $t("form.imageAlignment") }}</label>
            <div class="button-group">
                <button class="button" :class="{ active: imageAlign === 'left' }" @click="updateImageAlign('left')">
                    {{ $t("form.left") }}
                </button>
                <button class="button" :class="{ active: imageAlign === 'center' }" @click="updateImageAlign('center')">
                    {{ $t("tables.center") }}
                </button>
                <button class="button" :class="{ active: imageAlign === 'right' }" @click="updateImageAlign('right')">
                    {{ $t("form.right") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { uploadAsset } from "../../../../../api/assetUpload";
import axios from "axios";
import { mapActions, mapState } from "vuex";

export default {
    name: "ImageSettings",
    props: {
        item: Object,
    },
    data() {
        return {
            imageWidth: this.item.image.width || 150,
            imageHeight: this.item.image.height || 150,
            imageAlign: this.item.image.align || "center",
            logo: this.item.image.asset || "",
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
    },
    methods: {
        ...mapActions("forms", ["updateFormField"]),
        updateImageAlign(align) {
            this.imageAlign = align;
            this.$emit("update-image-align", align);
            this.updateField();
        },
        updateImageWidth(width) {
            this.imageWidth = width;
            this.$emit("update-image-width", width);
            this.updateField();
        },
        updateImageHeight() {
            this.$emit("update-image-height", this.imageHeight);
            this.updateField();
        },
        async uploadFile(e) {
            let {
                files: [file],
            } = document.getElementById(e.target.id);
            if (!this.beforeUpload(file)) return;
            let path = `${this.site._id}/form/`;
            try {
                let imdUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "form",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: imdUploadData.data.method,
                    url: imdUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                this.logo = `https://static.supsis.live/${imdUploadData.data.asset.path + imdUploadData.data.asset._id}.${imdUploadData.data.asset.ext}`;
                this.updateField();
                this.$emit("update-image-url", this.logo);
                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });

            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            }
        },
        beforeUpload(file) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            if (!isJpgOrPng) {
                this.$toasted.show(this.$t("form.onlyImageUpload"), {
                    duration: 3000,
                });
            }
            const isLt5M = file.size / 1024 / 1024 < 50;
            if (!isLt5M) {
                this.$toasted.show(this.$t("form.imageSizeLimit"), {
                    duration: 3000,
                });
            }
            return isJpgOrPng && isLt5M;
        },
        updateField() {
            const updateFields = {
                image: {
                    asset: this.logo,
                    width: this.imageWidth,
                    height: this.imageHeight,
                    align: this.imageAlign,
                },
            };
            this.updateFormField({
                siteId: this.item.siteId,
                formFieldId: this.item._id,
                updateFields,
            });
        },
    },
};
</script>

<style scoped>
#image-settings {
    padding: 15px;
}

.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
}

.form-label {
    margin-bottom: 5px;
    font-weight: bold;
    color: #333;
}

.form-input {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #f9f9f9;
    color: #333;
    font-size: 16px;
    flex: 1;
}

.size-settings {
    display: flex;
    flex-direction: column;
}

.size-input {
    margin-bottom: 15px;
}

.input-with-unit {
    display: flex;
    align-items: center;
}

.input-with-unit input {
    flex: 1;
}

.unit {
    margin-left: 5px;
    color: #333;
    font-size: 16px;
}

.button-group {
    display: flex;
}

.button {
    flex: 1;
    padding: 10px;
    margin-right: 5px;
    border: none;
    border-radius: 4px;
    background-color: #e0e0e0;
    color: #333;
    font-size: 14px;
    cursor: pointer;
}

.button.active {
    background-color: #4a90e2;
    color: #fff;
}

.button:last-child {
    margin-right: 0;
}
</style>
